import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { Link, Navigate } from 'react-router-dom';

import ErrorWrapper from '../../ErrorWrapper';
import {
  usePromiseWrapper,
  useCustomTranslation,
  useStoreSelector,
  useStoreValidator,
  useRouter,
  useCache,
  useActions,
} from '../../../helpers/hooks';
import { fetchPayWaysForPayMethod } from '../../../actions/payMethods';
import { fetchPayWay, selectPayWay } from '../../../actions/payWays';

import { ReactComponent as ArrowLeft } from '../../../imgs/arrow-left-icon.svg';
import CreationCard from './CreationCard';
import DescriptionCard from './DescriptionCard';
import ManualCard from './ManualCard';
import WarningCard from './WarningCard';
import Loader from '../../Loader';
import { ErrorBlock } from '../../Block';

function selectDataForPm(id) {
  return (selector) => {
    let payMethod = {};
    try {
      payMethod = selector.getPayMethod(id);
    } catch (e) {}

    let siblingPayWays = [];
    try {
      siblingPayWays = selector.getPayWaysForPayMethod(id);
    } catch (e) {}

    let payWay = {};
    try {
      payWay = selector.getSelectedPayWay();
    } catch (e) {
      if (siblingPayWays.length !== 0) {
        payWay = selector.getPayWay(siblingPayWays[0].id);
      }
    }

    return {
      payMethod,
      payWay: {
        ...payWay,
        details: payWay.details || {},
      },
      siblingPayWays,
    };
  };
}

export default function PayWaySelectorContainer() {
  const {
    location: { pathname },
  } = useRouter();
  const pmId = pathname.split('/').pop();

  let { payWay, payMethod, siblingPayWays } = useStoreSelector(
    selectDataForPm(pmId),
    [pmId]
  );

  const payWayPresentInPayment = useStoreValidator((validator) =>
    validator.payWayPresentInPayment()
  );

  const actions = useActions(
    { fetchPayWay, selectPayWay, fetchPayWaysForPayMethod },
    []
  );

  const pwId = payWay.id;

  const validateCache = useCache();

  const [wrappedDetailFetch, detailLoading, detailError] = usePromiseWrapper({
    initPromise: actions.fetchPayWay,
    initialState: {
      loading: !(typeof pwId == 'undefined' || validateCache('payWay', pwId)),
    },
  });

  useEffect(() => {
    if (typeof pwId == 'undefined' || validateCache('payWay', pwId)) return;

    wrappedDetailFetch(pwId);
  }, [typeof pwId == 'undefined']);

  const pmFetchNeeded = !(
    payWayPresentInPayment || validateCache('payMethodPayWays', pmId)
  );

  const [wrappedSiblingsFetch, siblingsLoading, siblingsError] =
    usePromiseWrapper({
      initPromise: actions.fetchPayWaysForPayMethod,
      initialState: {
        loading: pmFetchNeeded,
      },
    });

  useEffect(() => {
    if (!pmFetchNeeded) return;

    wrappedSiblingsFetch(pmId);
  }, []);

  const { urlFor, t } = useCustomTranslation();

  const [runMoveOutAnimation, setAnimateMoveOut] = useState(false);
  function animateMoveOut() {
    setAnimateMoveOut(true);
  }

  if (siblingsError) {
    return <Navigate to={urlFor('payMethodSelector')} />;
  }

  if (payWayPresentInPayment) {
    siblingPayWays = [
      {
        id: payWay.id,
        currency: payWay.currency,
      },
    ];
  }

  function validateId(value) {
    return value != null;
  }

  const showFullError =
    detailError &&
    (!detailError?.data?.errCode ||
      [101, 102, 103, 104, 105].indexOf(detailError.data.errCode) === -1);

  async function handlePayWayChange(id) {
    const f = wrappedDetailFetch.bind({
      cached() {
        return validateCache('payWay', id);
      },
    });

    await f(id);

    actions.selectPayWay(id);
  }

  const errorMessage =
    detailError?.data && 'message' in detailError.data
      ? t('DescriptionErrorCode', {
          description: detailError.data.message,
        })
      : null;

  const redesignedError = detailError?.data?.errCode === 41;

  return (
    <section className={classnames({ 'block--moveout': runMoveOutAnimation })}>
      {payWayPresentInPayment ? null : (
        <Link to={urlFor('payMethodSelector')} className="link link--inline">
          <ArrowLeft className="arrow-back" /> {t('PayWays.Back')}
        </Link>
      )}

      {showFullError ? (
        <ErrorWrapper error={detailError} type="PayWays">
          {() => (
            <ErrorBlock
              headerText={t('ErrorCode', {
                errorCode: detailError.data.errCode,
              })}
              subheader={redesignedError ? null : errorMessage}
              text={redesignedError ? errorMessage : t('PayWays.Errors.Support')}
              redesigned={redesignedError}
            />
          )}
        </ErrorWrapper>
      ) : (
        <>
          {detailLoading || siblingsLoading ? <Loader /> : null}

          {validateId(payWay.details.warning) ? (
            <WarningCard id={payWay.details.warning} />
          ) : null}

          <CreationCard
            payWay={payWay}
            detailError={detailError}
            selectPayWay={selectPayWay}
            siblingPayWays={siblingPayWays}
            payMethod={payMethod}
            handlePayWayChange={handlePayWayChange}
            onRedirect={animateMoveOut}
          />

          {validateId(payWay.details.description) ? (
            <DescriptionCard id={payWay.details.description} />
          ) : null}

          {validateId(payWay.details.manual) ? (
            <ManualCard id={payWay.details.manual} />
          ) : null}
        </>
      )}
    </section>
  );
}
